"use client";

import { CollectionCard } from "modules/collections/details/model/collection";
import { useCardByDataId } from "modules/cards/hooks/useCardByDataId";
import { FinishEnum, GameCard } from "modules/cards/model/card";
import { TailwindTooltipCardImage } from "modules/common/components/TailwindTooltipCardImage";
import React, { useState } from "react";
import { TradeItem } from "modules/tradeLists/model/tradeList";
import { cn } from "utils/utils";
import { hasFoilVisual } from "modules/cards/model/finishBehavior";
import type { GameId } from "modules/common/model/models";

interface FetchCardDataNameCellProps {
  basicCard: CollectionCard | TradeItem;
  game: GameId;
  name: string;
  fullWidth?: boolean;
  isFoil?: boolean;
  onClick: () => void;
}

type CardDataNameCellViewProps = Omit<
  FetchCardDataNameCellProps,
  "game" | "basicCard"
> & {
  basicCard: CollectionCard | TradeItem | GameCard;
  onPrefetch?: () => void;
};

function CardDataNameCellView({
  basicCard,
  name,
  fullWidth = false,
  isFoil,
  onClick,
  onPrefetch,
}: CardDataNameCellViewProps) {
  const foil = isFoil ?? hasFoilVisual(basicCard.finishes);

  return (
    <div
      className={cn({
        "max-w-[130px]": true,
        // fullWidth: stretch to the parent cell so the button below can run
        // its `overflow-hidden text-ellipsis` against a bounded width. Using
        // `w-fit` here would let the wrapper grow past the cell — clipping
        // would still happen via the parent's overflow-hidden, but the
        // ellipsis wouldn't render because the truncating element wouldn't
        // be the constrained one.
        "min-md:w-full! min-md:max-w-none!": fullWidth,
      })}
    >
      <TailwindTooltipCardImage
        card={basicCard}
        isFoil={foil}
        placement="right"
      >
        <button
          className="text-primary w-full overflow-hidden text-ellipsis whitespace-nowrap text-left hover:underline"
          onClick={onClick}
          onMouseEnter={onPrefetch}
          onFocus={onPrefetch}
        >
          {name}
        </button>
      </TailwindTooltipCardImage>
    </div>
  );
}

export function CardDataNameCell(props: CardDataNameCellViewProps) {
  return <CardDataNameCellView {...props} />;
}

export function FetchCardDataNameCell(props: FetchCardDataNameCellProps) {
  const { basicCard, game } = props;
  const dataId = basicCard.dataId;
  const [shouldFetch, setShouldFetch] = useState(false);
  useCardByDataId({ dataId, game, enabled: shouldFetch });

  return (
    <CardDataNameCellView {...props} onPrefetch={() => setShouldFetch(true)} />
  );
}
